import Sheet from "components/sheet/Sheet";
import React from "react";
import { useTranslation } from "react-i18next";
import NewCustomer from "components/newCustomer/NewCustomer";
import { ICustomer } from "redux/reducers/models/customer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Select } from "components/select/index";
import {
  faArrowCircleDown,
  faCircleExclamation,
  faEnvelope,
  faPhone,
  faXmark,
} from "@fortawesome/pro-regular-svg-icons";
import { Badge } from "components/badge/Badge";
import "./changeCustomer.scss";

interface IChangeCustomerProps {
  open: boolean;
  title: string;
  currentCustomerTitle: string;
  newCustomerTitle: string;
  okText: string;
  changeCustomerState: any;
  searchResults: Array<ICustomer>;
  searching: boolean;
  newCustomerActive: boolean;
  onOk: () => void;
  onCancel: () => void;
  handleSearchQueryChange: (customerQuery: string) => void;
  handleCustomerSelection: (id: number, customer: ICustomer) => void;
  handleRemoveSelectedCustomer: () => void;
  handleCreateNewCustomer: (customerInfo: any) => void;
  handleCloseCreateNewCustomer: () => void;
  handleNewCustomerActive: () => void;
  /** Optional context shown in the replace confirmation line (e.g. account title). */
  contextLabel?: string;
}

type TCustomerDisplay = {
  full_name?: string;
  email?: string;
  phone?: string;
  member_code?: string;
  customer_type?: string;
  initials?: string;
  first_name?: string;
  last_name?: string;
  profile_image_source?: string | null;
  profile_image_source_url?: string | null;
};

function resolveCustomer(source: any): TCustomerDisplay | null {
  if (!source) {
    return null;
  }
  if (source.customer) {
    return source.customer as TCustomerDisplay;
  }
  return source as TCustomerDisplay;
}

function getCustomerInitials(customer: TCustomerDisplay): string {
  if (customer.initials?.trim()) {
    return customer.initials.trim().slice(0, 2).toUpperCase();
  }

  const first = customer.first_name?.charAt(0) ?? "";
  const last = customer.last_name?.charAt(0) ?? "";
  const initials = `${first}${last}`.toUpperCase();
  if (initials) {
    return initials;
  }

  return customer.full_name?.trim()?.charAt(0)?.toUpperCase() || "?";
}

function getCustomerSubtitle(customer: TCustomerDisplay): string {
  const type = customer.customer_type?.trim() || "";
  const memberCode = customer.member_code?.trim() || "";
  if (type && memberCode) {
    return `${type} • ${memberCode}`;
  }
  return type || memberCode || "—";
}

function CustomerDetailCard(props: {
  customer: TCustomerDisplay;
  selected?: boolean;
  reserveRemoveSpace?: boolean;
  onRemove?: () => void;
}) {
  const { t } = useTranslation();
  const { customer, selected, reserveRemoveSpace, onRemove } = props;
  const imageSource = customer.profile_image_source_url || customer.profile_image_source;

  return (
    <div
      className={`change-customer__card${selected ? " change-customer__card--selected" : ""}`}
    >
      <div className="change-customer__card-body">
        <div className="change-customer__identity">
          {imageSource ? (
            <img
              className="change-customer__avatar change-customer__avatar--image"
              src={imageSource}
              alt=""
            />
          ) : (
            <div className="change-customer__avatar" aria-hidden>
              {getCustomerInitials(customer)}
            </div>
          )}
          <div className="change-customer__details">
            <span className="change-customer__name">{customer.full_name || "—"}</span>
            <span className="change-customer__subtitle">{getCustomerSubtitle(customer)}</span>
          </div>
        </div>

        <div className="change-customer__card-divider" aria-hidden />

        <div className="change-customer__contact">
          {customer.phone ? (
            <div className="change-customer__contact-row">
              <FontAwesomeIcon icon={faPhone} className="change-customer__contact-icon" />
              <span>{customer.phone}</span>
            </div>
          ) : null}
          {customer.email ? (
            <div className="change-customer__contact-row">
              <FontAwesomeIcon icon={faEnvelope} className="change-customer__contact-icon" />
              <span>{customer.email}</span>
            </div>
          ) : null}
          {!customer.phone && !customer.email ? (
            <div className="change-customer__contact-row">
              <span>—</span>
            </div>
          ) : null}
        </div>
      </div>

      {onRemove ? (
        <button
          type="button"
          className="change-customer__remove-button"
          aria-label={t("Remove selected customer")}
          onClick={onRemove}
        >
          <FontAwesomeIcon icon={faXmark} />
        </button>
      ) : reserveRemoveSpace ? (
        <div className="change-customer__remove-spacer" aria-hidden />
      ) : null}
    </div>
  );
}

function ChangeCustomer(props: IChangeCustomerProps) {
  const { t } = useTranslation();
  const { Option } = Select;

  const currentCustomer = resolveCustomer(
    props.changeCustomerState.bookingParticipant?.customer
      ? props.changeCustomerState.bookingParticipant
      : props.changeCustomerState.currentCustomer,
  );
  const selectedCustomer = resolveCustomer(props.changeCustomerState.selectedCustomer);
  const hasCurrentCustomer = !!currentCustomer;
  const hasSelectedCustomer = !!selectedCustomer;

  function onOk() {
    void props.onOk();
  }

  function onCancel() {
    props.onCancel();
  }

  function handleCustomerSearch(query: string) {
    void props.handleSearchQueryChange(query);
  }

  function handleCustomerSelection(id: number, customer: ICustomer) {
    void props.handleCustomerSelection(id, customer);
  }

  function handleCreateNewCustomer(customerInfo: any) {
    void props.handleCreateNewCustomer(customerInfo);
  }

  function handleCloseCreateNewCustomer() {
    void props.handleCloseCreateNewCustomer();
  }

  function handleNewCustomerActive() {
    void props.handleNewCustomerActive();
  }

  return (
    <Sheet
      title={props.title}
      open={props.open}
      size="small"
      closable
      stacked
      onCancel={onCancel}
      onOk={onOk}
      okText={props.okText}
      okDisabled={!hasSelectedCustomer}
      overflow
    >
      {props.newCustomerActive ? (
        <NewCustomer
          newCustomerSheetActive={props.newCustomerActive}
          onCancel={handleCloseCreateNewCustomer}
          onOk={handleCreateNewCustomer}
          searchValue={props.changeCustomerState.customerQuery}
        />
      ) : (
        <div className="change-customer">
          {hasCurrentCustomer ? (
            <div className="change-customer__section">
              <p className="change-customer__section-title">{props.currentCustomerTitle}</p>
              <CustomerDetailCard customer={currentCustomer} reserveRemoveSpace={hasSelectedCustomer} />
            </div>
          ) : null}

          {hasCurrentCustomer ? (
            <div className="change-customer__transition" aria-hidden>
              <div className="change-customer__transition-line" />
              <div className="change-customer__transition-icon">
                <FontAwesomeIcon icon={faArrowCircleDown} />
              </div>
              <div className="change-customer__transition-line" />
            </div>
          ) : null}

          <div className="change-customer__section">
            <p className="change-customer__section-title">{props.newCustomerTitle}</p>

            {hasSelectedCustomer ? (
              <CustomerDetailCard
                customer={selectedCustomer}
                selected
                onRemove={props.handleRemoveSelectedCustomer}
              />
            ) : (
              <Select
                showSearch
                className="flex justify-center align-center w-full h-10 position-relative z-20 text-black text-medium appearance-none border-none focus:outline-none placeholder-gray-200"
                onSearch={(query: string) => handleCustomerSearch(query)}
                onChange={(id: number, customer: ICustomer) => handleCustomerSelection(id, customer)}
                placeholder={t("Search by name, email, phone or member number...")}
                allowClear
                searchValue={props.changeCustomerState.customerQuery}
                showDropDownOnFocus={true}
                searching={props.searching}
                autoFocus={props.open}
              >
                <div className="ui-select-dropdown-list-item" onClick={handleNewCustomerActive}>
                  <p>{t("New customer")}</p>
                </div>
                {props?.searchResults?.map((customer, index) => {
                  return (
                    <Option key={index} value={customer.id} name={customer.full_name} extraValues={customer}>
                      <div className="flex justify-between">
                        <div>
                          <div className="text-semibold text-lg">{customer?.full_name}</div>
                          <div className="text-sm text-gray-500">{customer.customer_type}</div>
                          <div className="text-sm text-gray-500">{customer.email}</div>
                          <div className="text-sm text-gray-500">{customer.phone ? customer.phone : null}</div>
                        </div>

                        <div className="flex flex-column justify-between">
                          <div className="flex flex-column gap-2">
                            {customer.has_high_priority_notes ? (
                              <div
                                className="font-medium text-error self-end gap-2 flex"
                                style={{ alignItems: "center" }}
                                onClick={e => {
                                  e.stopPropagation();
                                  window.open(`/admin/customers/${String(customer.id)}/notes`);
                                }}
                              >
                                <FontAwesomeIcon icon={faCircleExclamation} size="lg" />
                                <p>{t("Click to view notes")}</p>
                              </div>
                            ) : (
                              <div />
                            )}
                            {customer?.banned ? (
                              <div className="ml-auto">
                                <Badge size="small" type="warning">
                                  {t("Banned")}
                                </Badge>
                              </div>
                            ) : null}
                          </div>
                          <div className="font-medium text-base text-gray-500 self-end">
                            {customer.member_code}
                          </div>
                        </div>
                      </div>
                    </Option>
                  );
                })}
              </Select>
            )}
          </div>

          {hasCurrentCustomer && hasSelectedCustomer && props.contextLabel ? (
            <p className="change-customer__summary">
              {t("Replace {{currentName}} with {{newName}} for", {
                currentName: currentCustomer.full_name || "—",
                newName: selectedCustomer.full_name || "—",
              })}{" "}
              <span className="change-customer__summary-context">{props.contextLabel}</span>
            </p>
          ) : null}
        </div>
      )}
    </Sheet>
  );
}

export default ChangeCustomer;
